// Global uppercase-input normalizer (Feb 2026).
//
// User rule: "All data entry fields with alpha should be in upper case,
// including masters — except Remarks."
//
// Rather than edit 200+ individual inputs, we install ONE capture-phase
// listener on `document` that upper-cases every keystroke into any text-
// type input, EXCEPT the exclusion list below.
//
// Exclusion rules (any match ⇒ input is left alone):
//   • data-nouppercase attribute on the input itself (explicit opt-out)
//   • type = email, password, url, tel, number, date, time, datetime-local,
//     month, week, color, file, hidden, range, checkbox, radio
//   • field name / id / data-testid contains any of: remark, description,
//     note, instruction, address, email, phone, mobile, url, website, hex,
//     search, filter, query, password
//   • placeholder contains "search" or "filter"
//   • inside a <textarea> — Remarks & long-form fields already use these
//   • ancestor has data-nouppercase="tree" (opt-out for an entire section)
//
// The React value is updated via the native `value` setter so React's
// synthetic input event picks up the change and re-renders normally.

const EXCLUDE_TYPES = new Set([
  "email", "password", "url", "tel", "number", "date", "time",
  "datetime-local", "month", "week", "color", "file", "hidden",
  "range", "checkbox", "radio",
]);

const EXCLUDE_NAME_SUBSTRINGS = [
  "remark", "description", "note", "instruction", "address",
  "email", "phone", "mobile", "url", "website", "hex", "search",
  "filter", "query", "password", "hostname", "url",
];

const nativeInputSetter = Object.getOwnPropertyDescriptor(
  window.HTMLInputElement.prototype, "value"
).set;

const shouldSkip = (el) => {
  if (!el || el.tagName !== "INPUT") return true;
  if (el.dataset.nouppercase !== undefined) return true;
  if (el.closest("[data-nouppercase='tree']")) return true;
  const type = (el.type || "text").toLowerCase();
  if (EXCLUDE_TYPES.has(type)) return true;
  const nameTag = `${el.name || ""} ${el.id || ""} ${el.dataset.testid || ""} ${el.getAttribute("data-testid") || ""} ${el.getAttribute("aria-label") || ""}`.toLowerCase();
  for (const sub of EXCLUDE_NAME_SUBSTRINGS) {
    if (nameTag.includes(sub)) return true;
  }
  const ph = (el.placeholder || "").toLowerCase();
  if (ph.includes("search") || ph.includes("filter")) return true;
  return false;
};

let installed = false;

export function installUppercaseInputs() {
  if (installed) return;
  installed = true;
  document.addEventListener("input", (e) => {
    const el = e.target;
    if (shouldSkip(el)) return;
    const v = el.value;
    if (!v) return;
    const up = v.toUpperCase();
    if (up === v) return;
    // Preserve caret position so upper-casing mid-word doesn't jump the cursor.
    const start = el.selectionStart;
    const end = el.selectionEnd;
    nativeInputSetter.call(el, up);
    try { el.setSelectionRange(start, end); } catch (_) { /* number/date etc. */ }
    // Re-fire so React's onChange picks the upper-cased value up.
    el.dispatchEvent(new Event("input", { bubbles: true }));
  }, true);
}
